/**
 * Notification Adapter
 * Abstracts push notification subscription across platforms
 */

import type { Platform } from './platform';
import { apiClient } from '../api.client';
import { ElectronNotificationAdapter } from './electron-notification.adapter';
import { CapacitorNotificationAdapter } from './capacitor-notification.adapter';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface NotificationAdapter {
  isSupported(): boolean;
  requestPermission(): Promise<boolean>;
  subscribe(): Promise<boolean>;
  unsubscribe(): Promise<boolean>;
  isSubscribed(): Promise<boolean>;
}

class PwaNotificationAdapter implements NotificationAdapter {
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY;
  }

  async requestPermission(): Promise<boolean> {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    const permissionGranted = await this.requestPermission();
    if (!permissionGranted) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      });

      await apiClient.post('/push/subscribe', {
        subscription: subscription.toJSON(),
        device_type: 'web',
        platform: 'pwa',
      });

      return true;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return false;
    }
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await apiClient.delete('/push/unsubscribe', {
          data: { endpoint: subscription.endpoint },
        });
        await subscription.unsubscribe();
      }

      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push:', error);
      return false;
    }
  }

  async isSubscribed(): Promise<boolean> {
    if (!this.isSupported()) return false;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  }
}

let notificationAdapterInstance: NotificationAdapter | null = null;

export function createNotificationAdapter(platform: Platform): NotificationAdapter {
  if (notificationAdapterInstance) return notificationAdapterInstance;

  switch (platform) {
    case 'electron':
      notificationAdapterInstance = new ElectronNotificationAdapter();
      break;
    case 'capacitor':
      notificationAdapterInstance = new CapacitorNotificationAdapter();
      break;
    case 'pwa':
    default:
      notificationAdapterInstance = new PwaNotificationAdapter();
      break;
  }

  return notificationAdapterInstance;
}

export function getNotificationAdapter(): NotificationAdapter {
  if (!notificationAdapterInstance) {
    throw new Error('Notification adapter not initialized. Call createNotificationAdapter() first.');
  }
  return notificationAdapterInstance;
}
